/**
 * Player leaderboards for the Player stats tab — switchable between
 * Top Scorers / Assists / Yellow cards / Red cards.
 * All rows use the same placeholder avatar from the Zporti CDN; team kit
 * beside it comes from `getTeamKitConfig` using the player’s club name.
 */

import React, { useMemo, useState } from 'react'
import { Image, Text, View, useWindowDimensions } from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { getTeamKitConfig } from '../../../helpers/teamKit.helper'
import {
  AnimatedSegmentedControl,
  type AnimatedSegmentOption,
} from '../../ui/AnimatedSegmentedControl'
import { SoccerKit } from '../../ui/SoccerKit'
import type {
  LeagueAnalyticsBoards,
  PlayerLeaderboardEntry,
} from '../../../src/leagueDetail'
import { PLAYER_SILHOUETTE_SOURCE } from '../../../constants/assets'
import { getLeaguePlayerBoardsStyles } from './LeaguePlayerBoards.styles'

export interface LeaguePlayerBoardsProps {
  boards: LeagueAnalyticsBoards
  isLoading: boolean
}

type BoardKey = 'scorers' | 'assists' | 'yellow' | 'red'

const BOARD_OPTIONS: AnimatedSegmentOption<BoardKey>[] = [
  { key: 'scorers', label: 'Scorers' },
  { key: 'assists', label: 'Assists' },
  { key: 'yellow', label: 'Yellow' },
  { key: 'red', label: 'Red' },
]

const STAT_LABEL_BY_BOARD: Record<BoardKey, string> = {
  scorers: 'G',
  assists: 'A',
  yellow: 'YC',
  red: 'RC',
}

function PlayerRow({
  entry,
  statLabel,
  isLast,
  styles,
  kitSize,
}: {
  entry: PlayerLeaderboardEntry
  statLabel: string
  isLast: boolean
  styles: ReturnType<typeof getLeaguePlayerBoardsStyles>
  kitSize: number
}) {
  const kit = getTeamKitConfig(entry.teamName || 'Unknown')

  return (
    <View style={[styles.row, isLast && styles.rowLast]}>
      <View style={styles.rank}>
        <Text style={styles.rankText}>{entry.rank}</Text>
      </View>
      <View style={styles.playerCell}>
        <View style={styles.avatarKitRow}>
          <Image
            source={PLAYER_SILHOUETTE_SOURCE}
            style={styles.avatar}
            accessibilityIgnoresInvertColors
            accessibilityLabel={entry.playerName}
          />
          <View style={styles.teamKitColumn}>
            <SoccerKit
              primary={kit.primary}
              secondary={kit.secondary}
              type={kit.type}
              size={kitSize}
            />
          </View>
        </View>
        <View style={styles.playerMeta}>
          <Text style={styles.playerName} numberOfLines={1}>
            {entry.playerName}
          </Text>
          {entry.teamName.length > 0 && (
            <Text style={styles.teamName} numberOfLines={1}>
              {entry.teamName}
            </Text>
          )}
        </View>
      </View>
      <View style={styles.statCell}>
        <Text style={styles.statValue} accessibilityLabel={`${entry.value} ${statLabel}`}>
          {entry.value}
        </Text>
      </View>
    </View>
  )
}

export const LeaguePlayerBoards: React.FC<LeaguePlayerBoardsProps> = ({ boards, isLoading }) => {
  const theme = useTheme()
  const { width: windowWidth } = useWindowDimensions()
  const styles = useMemo(
    () => getLeaguePlayerBoardsStyles(theme, windowWidth),
    [theme, windowWidth],
  )
  const kitSize = windowWidth >= 1024 ? 22 : windowWidth >= 768 ? 20 : 18

  const [activeBoard, setActiveBoard] = useState<BoardKey>('scorers')

  const activeRows = useMemo<PlayerLeaderboardEntry[]>(() => {
    switch (activeBoard) {
      case 'scorers':
        return boards.topScorers
      case 'assists':
        return boards.topAssists
      case 'yellow':
        return boards.topYellowCards
      case 'red':
        return boards.topRedCards
    }
  }, [activeBoard, boards])

  const statLabel = STAT_LABEL_BY_BOARD[activeBoard]

  return (
    <View style={styles.container}>
      <View style={styles.modeBarRow}>
        <AnimatedSegmentedControl<BoardKey>
          variant="sportsBar"
          options={BOARD_OPTIONS}
          value={activeBoard}
          onChange={setActiveBoard}
          testID="league-player-boards-mode"
        />
      </View>

      <View style={styles.card}>
        <View style={styles.headerRow}>
          <View style={styles.headerRank}>
            <Text style={styles.headerLabel}>#</Text>
          </View>
          <View style={styles.headerPlayer}>
            <Text style={styles.headerLabel}>Player</Text>
          </View>
          <View style={styles.headerStat}>
            <Text style={styles.headerLabelEnd}>{statLabel}</Text>
          </View>
        </View>

        {activeRows.length === 0 ? (
          <View style={styles.emptyWrap}>
            <Text style={styles.emptyText}>
              {isLoading ? 'Loading leaderboard…' : 'No data available yet.'}
            </Text>
          </View>
        ) : (
          activeRows.map((entry, idx) => (
            <PlayerRow
              key={`${entry.playerId ?? 'x'}-${entry.rank}`}
              entry={entry}
              statLabel={statLabel}
              isLast={idx === activeRows.length - 1}
              styles={styles}
              kitSize={kitSize}
            />
          ))
        )}
      </View>
    </View>
  )
}
